import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { UploadCloud, X, Check, AlertTriangle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileUrl = (path) => `${API}/files/${path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`;

export default function BulkUpload({ customers = [] }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [reviewEdits, setReviewEdits] = useState({}); // photo_path -> { customer_email, tracking_number }
  const [health, setHealth] = useState(null);

  const customerOptions = useMemo(
    () => [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [customers],
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/integrations/health`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setHealth(res.data))
      .catch(() => setHealth({ ready_for_bulk_upload: false, emergent_llm_key: false, object_storage: false }));
  }, []);

  const handleFiles = (list) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const arr = Array.from(list).filter(f => allowed.includes(f.type));
    if (arr.length === 0) {
      toast.error('Please select JPEG / PNG / WEBP images');
      return;
    }
    if (arr.length > 100) {
      toast.error('Maximum 100 images per batch');
      return;
    }
    setFiles(arr);
    setResult(null);
    setReviewEdits({});
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const startUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/admin/packages/bulk-upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5 * 60 * 1000, // 5 minutes for big batches
      });
      setResult(res.data);
      // Seed review edits from suggestions
      const edits = {};
      (res.data.review || []).forEach(r => {
        edits[r.photo_path] = {
          customer_email: r.suggested_customer?.email || '',
          tracking_number: r.tracking_number || '',
        };
      });
      setReviewEdits(edits);
      toast.success(
        `${res.data.matched_count} auto-matched, ${res.data.review_count} need review, ${res.data.duplicate_count} duplicates`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    }
    setUploading(false);
  };

  const setEdit = (photo_path, key, value) => {
    setReviewEdits(prev => ({ ...prev, [photo_path]: { ...prev[photo_path], [key]: value } }));
  };

  const confirmReview = async () => {
    if (!result || !result.review) return;
    const items = result.review
      .map(r => ({
        photo_path: r.photo_path,
        tracking_number: (reviewEdits[r.photo_path]?.tracking_number || '').trim(),
        customer_email: (reviewEdits[r.photo_path]?.customer_email || '').trim(),
      }))
      .filter(it => it.customer_email && it.tracking_number);
    if (items.length === 0) {
      toast.error('Assign at least one customer + tracking number first');
      return;
    }
    setConfirming(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/packages/bulk-confirm`, { items }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${res.data.created_count} packages created (${res.data.skipped_count} skipped)`);
      // Remove confirmed items from review list
      const confirmedPaths = new Set(res.data.created.map(c => items.find(it => it.tracking_number === c.tracking_number)?.photo_path).filter(Boolean));
      setResult(prev => prev ? { ...prev, review: prev.review.filter(r => !items.some(it => it.photo_path === r.photo_path) || !confirmedPaths.has(r.photo_path)) } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    }
    setConfirming(false);
  };

  const reset = () => { setFiles([]); setResult(null); setReviewEdits({}); };

  return (
    <div className="space-y-6" data-testid="bulk-upload-section">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Bulk Package Upload</h2>
        <p className="text-sm text-slate-600 mt-1">
          Drop up to 100 package label photos. We OCR each one with Gemini, fuzzy-match the recipient to a customer, and auto-create the package on a high-confidence match (≥ 85%). Lower-confidence images land in <span className="font-semibold">Needs Review</span> for you to confirm.
        </p>
      </div>

      {/* Upload area */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <label
          htmlFor="bulk-file-input"
          className="block cursor-pointer border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 rounded-lg p-8 text-center transition-colors"
          data-testid="bulk-upload-dropzone"
        >
          <UploadCloud className="h-10 w-10 mx-auto text-slate-400 mb-2" />
          <p className="text-sm font-semibold text-slate-700">Click to choose images or drop them here</p>
          <p className="text-xs text-slate-500 mt-1">JPEG / PNG / WEBP · up to 100 per batch · max 10 MB each</p>
          <input
            id="bulk-file-input"
            data-testid="bulk-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
        </label>

        {files.length > 0 && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-medium text-slate-700">{files.length} image{files.length === 1 ? '' : 's'} ready</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={reset} disabled={uploading}>Clear</Button>
                <Button
                  size="sm"
                  onClick={startUpload}
                  disabled={uploading}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="bulk-start-upload-btn"
                >
                  {uploading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Processing…</> : <>Process {files.length} image{files.length === 1 ? '' : 's'}</>}
                </Button>
              </div>
            </div>
            {!uploading && (
              <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative group">
                    <img
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      className="h-16 w-full object-cover rounded border border-slate-200"
                    />
                    <button
                      onClick={() => removeFile(i)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="Auto-matched" value={result.matched_count} accent="emerald" />
            <SummaryCard label="Needs Review" value={result.review_count} accent="amber" />
            <SummaryCard label="Duplicates" value={result.duplicate_count} accent="slate" />
            <SummaryCard label="Errors" value={result.error_count} accent="red" />
          </div>

          {result.matched.length > 0 && (
            <ResultGroup title="Auto-matched (already attached)" badge="emerald" items={result.matched}>
              {result.matched.map(r => (
                <ResultRow key={r.photo_path} item={r}>
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Matched · {Math.round(r.match_score)}%</Badge>
                  <p className="text-xs text-slate-600 mt-1">{r.matched_customer_email}</p>
                </ResultRow>
              ))}
            </ResultGroup>
          )}

          {result.duplicates.length > 0 && (
            <ResultGroup title="Duplicates (already in the system, skipped)" badge="slate" items={result.duplicates}>
              {result.duplicates.map(r => (
                <ResultRow key={r.photo_path} item={r}>
                  <Badge variant="outline">Duplicate</Badge>
                  <p className="text-xs text-slate-500 mt-1">{r.matched_customer_email || r.suggested_customer?.email}</p>
                </ResultRow>
              ))}
            </ResultGroup>
          )}

          {result.review.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-xl">
              <div className="px-4 py-3 flex items-center justify-between border-b border-amber-100 bg-amber-50 rounded-t-xl flex-wrap gap-2">
                <h3 className="font-semibold text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Needs Review · {result.review.length}</h3>
                <Button
                  size="sm"
                  onClick={confirmReview}
                  disabled={confirming}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="bulk-confirm-review-btn"
                >
                  {confirming ? 'Saving…' : 'Confirm All Assigned'}
                </Button>
              </div>
              <div className="divide-y divide-slate-100">
                {result.review.map(r => {
                  const edit = reviewEdits[r.photo_path] || {};
                  return (
                    <div key={r.photo_path} className="p-4 flex flex-col sm:flex-row gap-4">
                      <img src={fileUrl(r.photo_path)} alt={r.filename} className="h-24 w-24 sm:h-28 sm:w-28 object-cover rounded border border-slate-200 flex-shrink-0 cursor-pointer" onClick={() => window.open(fileUrl(r.photo_path), '_blank')} />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex flex-wrap gap-2 items-center">
                          <Badge className={r.confidence === 'medium' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}>
                            {r.confidence === 'medium' ? `Maybe · ${Math.round(r.match_score)}%` : 'Unknown'}
                          </Badge>
                          <span className="text-xs text-slate-500 truncate">{r.filename}</span>
                        </div>
                        <p className="text-xs text-slate-600">
                          OCR read: <span className="font-mono">{r.extracted_name || '—'}</span>
                          {r.suggested_customer && (
                            <> · Suggested: <span className="font-semibold">{r.suggested_customer.name}</span> <span className="text-slate-400">({r.suggested_customer.unique_id})</span></>
                          )}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500">Tracking Number</label>
                            <Input
                              value={edit.tracking_number || ''}
                              onChange={e => setEdit(r.photo_path, 'tracking_number', e.target.value)}
                              placeholder="Tracking number"
                              className="h-9"
                              data-testid={`review-tracking-${r.photo_path}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Assign to Customer</label>
                            <select
                              value={edit.customer_email || ''}
                              onChange={e => setEdit(r.photo_path, 'customer_email', e.target.value)}
                              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                              data-testid={`review-customer-${r.photo_path}`}
                            >
                              <option value="">— pick customer —</option>
                              {customerOptions.map(c => (
                                <option key={c.email} value={c.email}>
                                  {c.name} · {c?.us_address?.unique_id || c.email}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-semibold text-red-900 mb-2">Errors ({result.errors.length})</h3>
              <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                {result.errors.map((e, i) => <li key={i}>{e.filename || 'unknown'}: {e.error || 'unknown error'}</li>)}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={reset}>Start a new batch</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  const palettes = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${palettes[accent] || palettes.slate}`}>
      <p className="text-xs uppercase tracking-wider font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function ResultGroup({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function ResultRow({ item, children }) {
  return (
    <div className="p-3 flex items-center gap-3">
      <img src={fileUrl(item.photo_path)} alt={item.filename} className="h-12 w-12 object-cover rounded border border-slate-200 flex-shrink-0 cursor-pointer" onClick={() => window.open(fileUrl(item.photo_path), '_blank')} />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
          {item.tracking_number || <span className="text-slate-400">(no tracking)</span>}
          <Check className="h-3.5 w-3.5 text-emerald-500" />
        </p>
        <p className="text-xs text-slate-500 truncate">{item.filename}</p>
      </div>
      <div className="text-right flex-shrink-0">{children}</div>
    </div>
  );
}
